import { useQueries } from '@tanstack/react-query';
import api from '../../api/client';
import { queryKeys } from './queryKeys';

export function buildRubricsCacheKey(rubrics = []) {
  return rubrics
    .map((rubric) => `${rubric.slug}:${rubric.cardSize || 'M'}:${rubric.label || ''}:${rubric.order ?? ''}`)
    .join('|');
}

export function buildCatalogRubricRow(type, rubric, items) {
  if (type === 'video') {
    const firstSection = (items || [])[0];
    return {
      id: rubric.slug,
      name: rubric.label,
      cardSize: rubric.cardSize || 'M',
      order: rubric.order,
      sectionType: 'VIDEO',
      presets: firstSection?.presets || [],
    };
  }

  const seen = new Set();
  const presets = [];
  for (const group of items || []) {
    for (const preset of group.presets || []) {
      if (!preset?.id || seen.has(preset.id)) continue;
      seen.add(preset.id);
      presets.push(preset);
    }
  }

  return {
    id: rubric.slug,
    name: rubric.label,
    cardSize: rubric.cardSize || 'M',
    order: rubric.order,
    sectionType: 'PHOTO',
    presets,
  };
}

export async function fetchCatalogRubricRow(type, rubric, signal) {
  const res = await api.get(`/content/catalog/${type}`, {
    params: { rubric: rubric.slug },
    signal,
  });
  return buildCatalogRubricRow(type, rubric, res.data?.items || []);
}

export function useCatalogRubricRows(type, rubrics, { enabled = true } = {}) {
  const queries = useQueries({
    queries: (rubrics || []).map((rubric) => ({
      queryKey: queryKeys.catalogRubricRow(type, rubric),
      queryFn: ({ signal }) => fetchCatalogRubricRow(type, rubric, signal),
      enabled: enabled && Boolean(type) && Boolean(rubric?.slug),
    })),
  });

  const rows = queries
    .map((query) => query.data)
    .filter((row) => row && Array.isArray(row.presets) && row.presets.length > 0);

  return {
    rows,
    isLoading: queries.some((query) => query.isLoading),
    isFetching: queries.some((query) => query.isFetching),
    isError: queries.some((query) => query.isError),
    errors: queries
      .map((query, index) => (query.isError ? { rubric: rubrics[index], error: query.error } : null))
      .filter(Boolean),
    refetch: () => Promise.all(queries.map((query) => query.refetch())),
  };
}
